import { AtSign, Building2, CreditCard, Hash, Landmark, Mail, MapPin } from 'lucide-react';
import { PropTextRow } from '../../shared/property-rows';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { AddressAutocomplete, type AddressDetails } from '../../ui/AddressAutocomplete';
import { streetFromPlace } from '../../../lib/utils/addressFromPlace';

/** Campi fiscali semplici gestiti a riga singola (label corta per la colonna w-32). */
const SIMPLE_FIELDS: Array<{ key: string; label: string; type: string; icon: React.ComponentType<any> }> = [
  { key: 'business_name', label: 'Ragione sociale', type: 'text', icon: Building2 },
  { key: 'vat_number', label: 'P.IVA', type: 'text', icon: Hash },
  { key: 'tax_code', label: 'Cod. fiscale', type: 'text', icon: CreditCard },
  { key: 'sdi_code', label: 'Codice SDI', type: 'text', icon: Landmark },
  { key: 'pec', label: 'PEC', type: 'email', icon: AtSign },
  { key: 'email', label: 'Email', type: 'email', icon: Mail },
  { key: 'email_amministrazione', label: 'Mail amministr.', type: 'email', icon: Mail },
];

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

interface ClientDataRowsProps {
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onChangeMany: (fields: Record<string, string>) => void;
  requiredFields: string[];
  showErrors: boolean;
  missingFields: string[];
  country?: string | null;
}

/**
 * Sezione "Dati cliente" dei drawer contratto (accettazione e gate) a righe di
 * proprietà: campi fiscali richiesti + indirizzo con autocomplete Google.
 * Sostituisce le griglie 2 colonne di AcceptanceStepClient/ClientRequiredFieldsModal.
 */
export function ClientDataRows({ values, onChange, onChangeMany, requiredFields, showErrors, missingFields, country }: ClientDataRowsProps) {
  const showAddress = requiredFields.includes('address');
  const addrMissing = (f: string) => showErrors && missingFields.includes('address') && !(values[f] ?? '').trim();

  // Il campo autocomplete mostra SOLO la via: il civico ha la sua riga sotto.
  // Prima mostrava "via civico" ma scriveva tutto in street_address, e a ogni
  // correzione a mano il civico finiva due volte nel contratto ("Via Roma 12 12").
  const addressValue = values.street_address ?? '';

  const applyPlace = (place: AddressDetails) => {
    // Google non sempre restituisce CAP o provincia (es. civico non mappato):
    // in quel caso si tiene quello che c'era invece di svuotarlo.
    // CAP e provincia vecchi solo se la città resta la stessa, altrimenti si svuotano.
    const city = place.city && place.city.trim() !== '' ? place.city : (values.city ?? '');
    const sameCity = city.trim().toLowerCase() === (values.city ?? '').trim().toLowerCase();
    const keep = (next: string | undefined, field: string) =>
      next && next.trim() !== '' ? next : sameCity ? (values[field] ?? '') : '';
    onChangeMany({
      // Senza "route" Google: primo pezzo dell'indirizzo, come in ClientForm
      street_address: streetFromPlace(place),
      street_number: place.street_number ?? '',
      postal_code: keep(place.postal_code, 'postal_code'),
      city,
      province: keep(place.province, 'province'),
    });
  };

  return (
    <div>
      {SIMPLE_FIELDS.filter((f) => requiredFields.includes(f.key)).map((f) => {
        const missing = showErrors && missingFields.includes(f.key);
        // Validazione leggera: il Codice SDI non è una email/PEC.
        const sdiLooksWrong = f.key === 'sdi_code' && (values[f.key] ?? '').includes('@');
        return (
          <PropTextRow
            key={f.key}
            icon={rowIcon(f.icon)}
            label={f.label}
            required
            type={f.type}
            value={values[f.key] ?? ''}
            onChange={(v) => onChange(f.key, v)}
            error={
              sdiLooksWrong
                ? 'Il Codice SDI è di 6-7 caratteri, non un indirizzo email. Per le aziende con PEC usa 0000000.'
                : missing ? 'Obbligatorio' : undefined
            }
          />
        );
      })}

      {showAddress && (
        <>
          <div>
            <PropRow icon={rowIcon(MapPin)} label="Indirizzo" required>
              <div className="min-w-0 flex-1">
                <AddressAutocomplete
                  label=""
                  bare
                  value={addressValue}
                  onChange={(v) => onChange('street_address', v)}
                  onPlaceSelected={applyPlace}
                  country={country || 'Italia'}
                  placeholder="Inizia a digitare l'indirizzo..."
                />
              </div>
            </PropRow>
            {addrMissing('street_address') && <p className="ml-[8.5rem] text-[11px] text-red-500">Obbligatorio</p>}
          </div>
          <PropTextRow icon={rowIcon(Hash)} label="Civico" value={values.street_number ?? ''}
            onChange={(v) => onChange('street_number', v)} />
          <PropTextRow icon={rowIcon(Hash)} label="CAP" value={values.postal_code ?? ''}
            onChange={(v) => onChange('postal_code', v)} error={addrMissing('postal_code') ? 'Obbligatorio' : undefined} />
          <PropTextRow icon={rowIcon(Building2)} label="Città" value={values.city ?? ''}
            onChange={(v) => onChange('city', v)} error={addrMissing('city') ? 'Obbligatorio' : undefined} />
          <PropTextRow icon={rowIcon(MapPin)} label="Provincia" value={values.province ?? ''}
            onChange={(v) => onChange('province', v)} error={addrMissing('province') ? 'Obbligatorio' : undefined} />
        </>
      )}
    </div>
  );
}
